import React from 'react';
import { Box, Checkbox } from '@mui/material';
import { FlexGridCell, FlexGridCheckboxCell, FlexGridRow } from 'global/components/flex-grid-list';
import { get, set, size } from 'global/lib/lodash';
import { Loading } from 'global/styles';
import { getUserAppPermissions } from 'global/utils';
import moment from 'moment';
import { getRealmIcon } from 'global/components/droprealms';

export const RowHeader = (props) => {
  const { hasClient } = props;

  return (
    <FlexGridRow listItem header layout="columns" dense>
      <FlexGridCheckboxCell>
        <Checkbox indeterminate={props.indeterminate} onClick={props.selectAll} checked={props.allSelected} />
      </FlexGridCheckboxCell>
      {hasClient ? (
        <>
          <FlexGridCell width="33%">Name</FlexGridCell>
          <FlexGridCell width="33%">Email</FlexGridCell>
          <FlexGridCell width="33%">Access</FlexGridCell>
        </>
      ) : (
        <>
          <FlexGridCell width="30%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell width="40%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              Name
            </FlexGridCell>
            <FlexGridCell width="60%" hideBelow="md" sx={{ mr: 1, pb: 0.5 }}>
              Email
            </FlexGridCell>
          </FlexGridCell>
          <FlexGridCell width="45%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell width="50%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              Access
            </FlexGridCell>
            <FlexGridCell width="50%" hideBelow="md" sx={{ mr: 1, pb: 0.5 }}>
              Permissions
            </FlexGridCell>
          </FlexGridCell>
          <FlexGridCell width="18%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell width="50%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              Status
            </FlexGridCell>
            <FlexGridCell width="50%" hideBelow="md" sx={{ mr: 1, pb: 0.5 }}>
              Modified
            </FlexGridCell>
          </FlexGridCell>
        </>
      )}
    </FlexGridRow>
  );
};

export default (props) => {
  const { item, selected, hasClient, basicDisplay } = props;

  const getAccess = () => {
    const obj = {};
    (item.realms || [])
      .filter((r) => r)
      .forEach((r) => {
        set(obj, r.tier, [...get(obj, r.tier, []), r]);
      });
    const pluralize = size(obj.community) > 1;
    const plurBuilders = size(obj.builders) > 1;
    const plurDivision = size(obj.division) > 1;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {!!size(obj.builder) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Box component="span" sx={{ mr: '5px', display: 'inline-flex', alignItems: 'center' }}>
              {getRealmIcon({ tier: 'builder' })}
            </Box>
            <Box component="span">{`${size(obj.builder)} ${plurBuilders ? 'Builders' : 'Builder'}`}</Box>
          </Box>
        )}
        {!!size(obj.division) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Box component="span" sx={{ mr: '5px', display: 'inline-flex', alignItems: 'center' }}>
              {getRealmIcon({ tier: 'division' })}
            </Box>
            <Box component="span">{`${size(obj.division)} ${plurDivision ? 'Divisions' : 'Division'}`}</Box>
          </Box>
        )}
        {!!size(obj.community) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Box component="span" sx={{ mr: '5px', display: 'inline-flex', alignItems: 'center' }}>
              {getRealmIcon({ tier: 'community' })}
            </Box>
            <Box component="span">{`${size(obj.community)} ${pluralize ? 'Communities' : 'Community'}`}</Box>
          </Box>
        )}
      </Box>
    );
  };

  const getPermission = () => {
    const dp = getUserAppPermissions(item.permissions);
    return dp ? dp.map((d) => d.title).join(', ') : 'None';
  };

  const getUserGroups = () => {
    const dp = item.userGroups;
    return dp ? dp.map((d) => d.name).join(', ') : 'None';
  };

  const getStatus = () => {
    return get(item, 'status') === 1 ? 'Active' : 'De-Activated';
  };

  const getLastAccess = () => {
    return get(item, 'updatedAt') ? moment(get(item, 'updatedAt')).fromNow() : Loading;
  };

  const isSelected = () => {
    return get(selected, item.id) ? true : false;
  };

  const content = {
    name: get(item, 'name', Loading),
    email: get(item, 'email', Loading),
    access: getAccess(),
    permission: getPermission(),
    status: getStatus(),
    lastAccess: getLastAccess(),
    userGroups: getUserGroups(),
  };

  const onSelectItem = (e) => {
    e.stopPropagation();
    e.preventDefault();
    props.onSelectItem && props.onSelectItem(props);
  };

  const onSelectRow = () => {
    props.onSelectRow && props.onSelectRow(props);
  };

  return (
    <FlexGridRow onClick={onSelectRow} listItem layout="columns" dense clickable>
      <FlexGridCheckboxCell>
        <Checkbox onClick={onSelectItem} checked={isSelected()} />
      </FlexGridCheckboxCell>
      {hasClient ? (
        <>
          <FlexGridCell width={basicDisplay ? '50%' : '33%'}>{content.name}</FlexGridCell>
          <FlexGridCell width={basicDisplay ? '50%' : '33%'}>{content.email}</FlexGridCell>
          {!basicDisplay && (
            <FlexGridCell width="33%" sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              {content.access}
            </FlexGridCell>
          )}
        </>
      ) : (
        <>
          <FlexGridCell width="30%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell width="40%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              {content.name}
            </FlexGridCell>
            <FlexGridCell width="60%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              {content.email}
            </FlexGridCell>
          </FlexGridCell>
          <FlexGridCell width="45%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell
              width="50%"
              stackAt="md"
              sx={{ mr: 1, pb: 0.5, flexDirection: 'column', alignItems: 'flex-start' }}
            >
              {content.access}
              <Box sx={{ mb: 1 }}>{content.userGroups}</Box>
            </FlexGridCell>
            <FlexGridCell width="50%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              {content.permission}
            </FlexGridCell>
          </FlexGridCell>
          <FlexGridCell width="15%" sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch">
            <FlexGridCell width="50%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              {content.status}
            </FlexGridCell>
            <FlexGridCell width="50%" stackAt="md" sx={{ mr: 1, pb: 0.5 }}>
              {content.lastAccess}
            </FlexGridCell>
          </FlexGridCell>
        </>
      )}
    </FlexGridRow>
  );
};
